<script lang="ts" setup>
import Dialog from 'primevue/dialog';
import { AppTypography } from '~/components/base';
import type { SourceLink } from '~/types/Candidates';

const { sources } = defineProps<{
  sources: SourceLink[];
}>();

const visible = defineModel<boolean>('visible', { default: false });
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    header="Sources"
    data-testid="sources-dialog"
    class="app-modal sources-dialog w-full max-w-lg"
    :dismissable-mask="true"
  >
    <ul class="flex flex-col">
      <li
        v-for="source in sources"
        :key="source.id"
        class="sources-dialog__row"
      >
        <a
          v-if="source.url"
          :href="source.url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center justify-between gap-4 no-underline"
        >
          <div class="min-w-0">
            <AppTypography type="subheadline" element="p" class="mb-2 truncate">
              {{ source.label }}
            </AppTypography>
            <AppTypography
              type="footnote"
              element="p"
              class="sources-dialog__url truncate"
            >
              {{ source.url.replace(/^https?:\/\//, '') }}
            </AppTypography>
          </div>
          <Icon
            name="solar:square-top-down-linear"
            size="24"
            class="shrink-0"
          />
        </a>
        <div v-else class="flex items-center justify-between gap-4">
          <AppTypography type="body" element="p" class="truncate">
            {{ source.label }}
          </AppTypography>
        </div>
      </li>
    </ul>
  </Dialog>
</template>

<style scoped lang="scss">
.sources-dialog {
  &__row {
    padding: $spacing-lg $spacing-lg;
    border-bottom: 1px solid $complementary-color-shade-1;
    color: inherit;

    &:last-child {
      border-bottom: none;
    }

    // Figma: text/primary on the mobile sheet — the desktop dialog keeps the
    // default body color.
    @media (max-width: 639.98px) {
      :deep(.font-body) {
        color: #201e1a;
      }
    }
  }

  // Figma: text/secondary (#827b72) — a warm neutral, not the navy-palette
  // $text-color-tertiary token used elsewhere in the app.
  &__url {
    color: #827b72;
  }

  &__external {
    color: $text-color-primary;
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog chrome via the
  `.sources-dialog` class it carries on the .p-dialog panel. Kept narrow to
  this modal so it doesn't leak to other dialogs.
-->
<style lang="scss">
.sources-dialog.p-dialog {
  // Figma: this popup uses the 32px "2xl" radius, not the 16px shared by
  // every other .app-modal dialog — scoped here so it doesn't affect them.
  border-radius: $radius-2xl;

  .p-dialog-title {
    font-weight: 700;
  }

  .p-dialog-close-button {
    &:hover,
    &:enabled:hover {
      background: transparent;
    }

    // Desktop: persistent light circle behind the X, at rest — matching the
    // other modals' (Campaign/PartyFilterModal, CandidatePartyModal) desktop
    // treatment. PrimeVue's default close button only paints this on
    // `:hover`; Figma shows it unconditionally.
    @media (min-width: 640px) {
      width: 28px;
      height: 28px;
      padding: 0;
      color: $border-color-tag;
      background-color: $surface-color-bg-2;

      &:hover,
      &:enabled:hover {
        background-color: $surface-color-bg-2;
      }
    }
  }

  // Same warm sand sheet as every other modal (LocationPickerModal,
  // Campaign/PartyFilterModal, CandidatePartyModal), at every breakpoint.
  background-color: #ece3d1;

  .p-dialog-header,
  .p-dialog-content {
    background-color: #ece3d1;
  }

  @media (max-width: 639.98px) {
    .p-dialog-header {
      position: relative;
      justify-content: center;
    }

    .p-dialog-title {
      text-align: center;
    }

    .p-dialog-content {
      .sources-dialog__row {
        padding-right: 0px;
      }
    }

    .p-dialog-close-button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      // Bare X, no background — coloured to match every other modal's
      // mobile close button (Figma's text/primary on the sand sheet).
      color: $text-color-subtext;
    }
  }
}
</style>
